import React, { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { Link } from "react-router-dom";
import Popup from "../../components/client/Popup";
import { Power, Play, Pause, Terminal } from "lucide-react";
/* Spinner: trung tính (light: xám nhạt, dark: trắng) */
const Spinner = () => (
	<span
		className="
      inline-block w-4 h-4 rounded-full animate-spin
      border-2 border-gray-300 border-t-transparent
      dark:border-white/40 dark:border-t-transparent
    "
	/>
);
const PauseIcon = ({ className = "w-6 h-6", color = "currentColor" }) => (
	<svg
		className={className}
		fill="none"
		viewBox="0 0 24 24"
		stroke={color}
		strokeWidth="2"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
		/>
	</svg>
);

const PlayIcon = ({ className = "w-6 h-6", color = "currentColor" }) => (
	<svg
		className={className}
		fill="none"
		viewBox="0 0 24 24"
		stroke={color}
		strokeWidth="2"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M14.752 11.168l-5.197-3.028A1 1 0 008 9.028v5.944a1 1 0 001.555.832l5.197-3.028a1 1 0 000-1.664z"
		/>
	</svg>
);


const TerminalIcon = ({ className = "w-5 h-5", color = "currentColor" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 9l3 3-3 3m5 0h3M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z"
    />
  </svg>
);
/** IP chip: light = bg-gray-100, dark giữ như cũ */
const PrimaryIP = ({ floating_ips = [], fixed_ips = [] }) => {
	const primary = floating_ips?.[0] || fixed_ips?.[0] || "-";

	const handleCopy = async () => {
		if (!primary || primary === "-") return;
		try {
			await navigator.clipboard.writeText(primary);
		} catch {}
	};

	if (primary === "-")
		return <span className="text-gray-400 dark:text-gray-400">-</span>;

	return (
		<div className="inline-flex items-center gap-2">
			<span
				className="
          inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1
          bg-gray-100 text-blue-700 ring-gray-200
          dark:bg-gray-800/70 dark:text-blue-300 dark:ring-gray-700
        "
			>
				<span className="opacity-90">🌐</span>
				<span className="tabular-nums tracking-tight">{primary}</span>
			</span>
			<button
				type="button"
				onClick={handleCopy}
				title="Copy IP"
				aria-label="Copy IP"
				className="
          group rounded-md p-1.5 ring-1 transition
          bg-white hover:bg-gray-50 ring-gray-200 hover:ring-gray-300
          dark:bg-gray-800/40 dark:hover:bg-gray-800 dark:ring-gray-700 dark:hover:ring-gray-600
        "
			>
				<svg
					viewBox="0 0 24 24"
					fill="currentColor"
					className="h-4 w-4 text-gray-600 group-hover:text-gray-900 dark:text-gray-300 dark:group-hover:text-white"
				>
					<path d="M9 7a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2V7Zm-4 4a2 2 0 0 1 2-2h.5a.5.5 0 0 1 .5.5V11a4 4 0 0 0 4 4h1.5a.5.5 0 0 1 .5.5V17a2 2 0 0 1-2 2H7a4 4 0 0 1-4-4v-4Z" />
				</svg>
			</button>
		</div>
	);
};

/** Badge: light dùng màu pastel + ring nhạt; dark giữ như cũ */
const StatusBadge = ({ status = "" }) => {
	const isOnline = String(status).toLowerCase() === "online";
	return (
		<span
			className={`
        inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ring-1
        ${
			isOnline
				? "bg-green-100 text-green-700 ring-green-200 dark:bg-green-900/40 dark:text-green-300 dark:ring-green-700"
				: "bg-red-100 text-red-700 ring-red-200 dark:bg-red-900/40 dark:text-red-300 dark:ring-red-700"
		}
      `}
		>
			<span
				className={`h-2 w-2 rounded-full ${
					isOnline ? "bg-green-500" : "bg-red-500"
				}`}
			/>
			{status}
		</span>
	);
};

const SkeletonRow = () => (
	<tr className="animate-pulse">
		{Array.from({ length: 7 }).map((_, idx) => (
			<td key={idx} className="px-6 py-4">
				<div className="h-4 rounded w-3/4 bg-gray-200 dark:bg-gray-700/70" />
			</td>
		))}
	</tr>
);

const formatDate = (d) => {
	try {
		return new Date(d).toLocaleDateString();
	} catch {
		return "-";
	}
};

/* Mobile card: light = card trắng; dark = như cũ */
const InstanceCard = ({ instance, onToggle, onConsole, loadingActions }) => {
	const { id, name, status, floating_ips, fixed_ips, plan, region, created } =
		instance;
	const isOnline = String(status).toLowerCase() === "online";
	const actionType = isOnline ? "stop" : "start";
	const isActionLoading = loadingActions?.[`${id}-${actionType}`];
	const isConsoleLoading = loadingActions?.[`${id}-console`];

	return (
		<div
			className="
        rounded-2xl p-4
        bg-white text-gray-800 shadow-md ring-1 ring-black/5
        dark:bg-black/30 dark:text-gray-300 dark:border dark:border-gray-700 dark:shadow-xl dark:ring-0 dark:backdrop-blur-md
      "
		>
			<div className="flex items-start justify-between gap-3">
				<Link
					to={`/client/vps/${id}`}
					className="text-base font-semibold text-gray-900 hover:underline dark:text-indigo-300 dark:hover:text-indigo-200"
				>
					{name}
				</Link>
				<StatusBadge status={status} />
			</div>

			<div className="mt-3 grid grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-300">
				<div className="space-y-1">
					<div className="text-gray-500 dark:text-gray-400">IP</div>
					<PrimaryIP
						floating_ips={floating_ips}
						fixed_ips={fixed_ips}
					/>
				</div>
				<div className="space-y-1">
					<div className="text-gray-500 dark:text-gray-400">
						Flavor
					</div>
					<div>{plan || "-"}</div>
				</div>
				<div className="space-y-1">
					<div className="text-gray-500 dark:text-gray-400">
						Region
					</div>
					<div>{region || "-"}</div>
				</div>
				<div className="space-y-1">
					<div className="text-gray-500 dark:text-gray-400">
						Created
					</div>
					<div title={new Date(created).toLocaleString()}>
						{formatDate(created)}
					</div>
				</div>
			</div>

			<div className="mt-4 flex items-center gap-2">
				<button
					onClick={() => onToggle(id, isOnline ? "stop" : "start")}
					disabled={isActionLoading}
					className={`
            inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg text-white ring-1 transition focus:outline-none focus:ring-2
            ${
				isOnline
					? "bg-red-600 hover:bg-red-700 ring-red-500 focus:ring-red-500"
					: "bg-green-600 hover:bg-green-700 ring-green-500 focus:ring-green-500"
			}
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
				>
					{isActionLoading ? (
						<Spinner />
					) : isOnline ? (
						<PauseIcon className="w-5 h-5 text-red-400" />
					) : (
						<PlayIcon className="w-5 h-5 text-green-400" />
					)}
				</button>
				<button
					onClick={() => onConsole(id)}
					disabled={isConsoleLoading}
					className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white ring-1 ring-blue-500 transition focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isConsoleLoading ? <Spinner /> : <TerminalIcon className="w-5 h-5" />}
				</button>
			</div>
		</div>
	);
};

const InstancesTable = ({ instances }) => {
	const [instanceList, setInstanceList] = useState([]);
	const [loadingActions, setLoadingActions] = useState({});
	const [popup, setPopup] = useState(null);

	useEffect(() => {
		if (Array.isArray(instances)) setInstanceList(instances);
	}, [instances]);

	const handleConsole = async (instanceId) => {
		const key = `${instanceId}-console`;
		setLoadingActions((p) => ({ ...p, [key]: true }));
		try {
			const res = await axiosInstance.post("/overview/console/", {
				server_id: instanceId,
				type: "novnc",
			});
			const consoleUrl = res.data?.console?.url;
			if (consoleUrl) {
				window.open(consoleUrl, "_blank", "noopener,noreferrer");
				setPopup({
					message: "Console opened in a new tab.",
					type: "success",
				});
			} else {
				setPopup({
					message: "Console URL not found in response.",
					type: "error",
				});
			}
		} catch (error) {
			setPopup({
				message: `Failed to open console: ${
					error.response?.data?.error || error.message
				}`,
				type: "error",
			});
		} finally {
			setLoadingActions((p) => ({ ...p, [key]: false }));
		}
	};

	const handleAction = async (instanceId, action) => {
		const key = `${instanceId}-${action}`;
		setLoadingActions((p) => ({ ...p, [key]: true }));
		try {
			await axiosInstance.post(
				`/openstack/compute/instances/${instanceId}/action/`,
				{ action }
			);
			setInstanceList((prev) =>
				prev.map((i) =>
					i.id === instanceId
						? {
								...i,
								status:
									action === "start" ? "Online" : "Offline",
						  }
						: i
				)
			);
			setPopup({
				message: `Action '${action}' executed successfully.`,
				type: "success",
			});
		} catch (err) {
			setPopup({
				message: `Failed to execute action '${action}'.`,
				type: "error",
			});
		} finally {
			setLoadingActions((p) => ({ ...p, [key]: false }));
		}
	};

	const isLoadingData = !Array.isArray(instances) || instances.length === 0;

	return (
		<section className="mx-auto py-5">
			<header className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<h2 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-green-400">
					My VPS
				</h2>
				<div className="flex items-center gap-2">
					<Link
						to="/create-instance"
						className="
              px-5 py-2 rounded-lg font-semibold text-white transition
              bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500
            "
					>
						Purchase VPS
					</Link>
				</div>
			</header>

			{popup && (
				<Popup
					message={popup.message}
					type={popup.type}
					onClose={() => setPopup(null)}
				/>
			)}

			{/* Desktop table wrapper: light card, dark giữ cũ */}
			<div
				className="
          hidden md:block overflow-x-auto rounded-2xl
          bg-white shadow-md ring-1 ring-black/5
          dark:bg-black/30 dark:backdrop-blur-lg dark:shadow-2xl dark:border dark:border-gray-700 dark:ring-0
        "
			>
				<table className="min-w-full text-sm">
					<thead
						className="
              text-gray-600 uppercase text-xs tracking-wider sticky top-0
              bg-gray-50
              dark:bg-gray-900/60 dark:text-gray-300 dark:backdrop-blur supports-[backdrop-filter]:dark:bg-gray-900/40
            "
					>
						<tr className="divide-x divide-transparent dark:divide-transparent">
							{[
								"Name",
								"Status",
								"IP",
								"Flavor",
								"Region",
								"Created",
								"Actions",
							].map((header) => (
								<th
									key={header}
									className="px-6 py-3 text-left font-semibold"
								>
									{header}
								</th>
							))}
						</tr>
					</thead>

					<tbody className="divide-y divide-gray-200 text-gray-800 dark:divide-gray-800 dark:text-gray-200">
						{isLoadingData ? (
							Array.from({ length: 5 }).map((_, i) => (
								<SkeletonRow key={i} />
							))
						) : instanceList.length === 0 ? (
							<tr>
								<td
									colSpan={7}
									className="py-12 text-center text-gray-500 dark:text-gray-500"
								>
									<div className="mx-auto max-w-md">
										<div className="mb-2 text-2xl">
											🚫 No instances found
										</div>
										<div className="text-sm">
											Create your first instance to get
											started.
										</div>
									</div>
								</td>
							</tr>
						) : (
							instanceList.map((instance) => {
								const {
									id,
									name,
									status,
									floating_ips,
									fixed_ips,
									plan,
									region,
									created,
								} = instance;
								const isOnline =
									String(status).toLowerCase() === "online";
								const actionType = isOnline ? "stop" : "start";

								// CHÚ Ý: không tạo string nào cho label, render icon trực tiếp
								const ActionIcon = isOnline ? (
									<PauseIcon className="w-5 h-5" />
								) : (
									<PlayIcon className="w-5 h-5" />
								);

								const isActionLoading =
									!!loadingActions[`${id}-${actionType}`];
								const isConsoleLoading =
									!!loadingActions[`${id}-console`];
                                
								return (
									<tr
										key={id}
										className="hover:bg-gray-50 transition dark:hover:bg-gray-900/40"
									>
										<td className="px-6 py-4 font-medium">
											<Link
												to={`/client/vps/${id}`}
												className="text-gray-900 hover:underline dark:text-indigo-300 dark:hover:text-indigo-200"
											>
												{name}
											</Link>
										</td>
										<td className="px-6 py-4">
											<StatusBadge status={status} />
										</td>
										<td className="px-6 py-4">
											<PrimaryIP
												floating_ips={floating_ips}
												fixed_ips={fixed_ips}
											/>
										</td>
										<td className="px-6 py-4">
											{plan || "-"}
										</td>
										<td className="px-6 py-4">
											{region || "-"}
										</td>
										<td
											className="px-6 py-4"
											title={new Date(
												created
											).toLocaleString()}
										>
											{formatDate(created)}
										</td>
										<td className="px-6 py-4">
											<div className="flex flex-wrap gap-2">
												<button
  onClick={() => handleAction(id, actionType)}
  disabled={isActionLoading}
  className="p-2 rounded-full border border-gray-600 text-gray-300 hover:bg-gray-700 transition disabled:opacity-40 flex items-center justify-center"
  title={isOnline ? "Shut Down" : "Power On"}
>
  {isActionLoading ? (
    <Spinner />
  ) : (
    <>
      {isOnline ? (
        <Power className="w-5 h-5" />
      ) : actionType === "pause" ? (
        <Pause className="w-5 h-5" />
      ) : (
        <Play className="w-5 h-5" />
      )}
    </>
  )}
</button>

<button
  onClick={() => handleConsole(id)}
  disabled={isConsoleLoading}
  className="p-2 rounded-full border border-gray-600 text-gray-300 hover:bg-gray-700 transition disabled:opacity-40 flex items-center justify-center"
  title="Open Console"
>
  {isConsoleLoading ? <Spinner /> : <Terminal className="w-5 h-5" />}
</button>

											</div>
										</td>
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>

			{/* Mobile layout wrapper: light card, dark giữ cũ */}
			<div className="md:hidden grid grid-cols-1 gap-3">
				{isLoadingData ? (
					Array.from({ length: 3 }).map((_, i) => (
						<div
							key={i}
							className="
                rounded-2xl p-4 animate-pulse
                bg-white shadow-md ring-1 ring-black/5
                dark:bg-black/30 dark:border dark:border-gray-700 dark:shadow-xl dark:ring-0 dark:backdrop-blur-md
              "
						>
							<div className="h-5 w-1/3 bg-gray-200 dark:bg-gray-700/70 rounded" />
							<div className="mt-3 grid grid-cols-2 gap-3">
								{Array.from({ length: 4 }).map((__, j) => (
									<div
										key={j}
										className="h-4 bg-gray-200 dark:bg-gray-700/70 rounded"
									/>
								))}
							</div>
							<div className="mt-4 h-9 bg-gray-200 dark:bg-gray-700/70 rounded" />
						</div>
					))
				) : instanceList.length === 0 ? (
					<div
						className="
              rounded-2xl p-8 text-center
              bg-white text-gray-600 shadow-md ring-1 ring-black/5
              dark:bg-black/30 dark:text-gray-400 dark:border dark:border-gray-700 dark:shadow-xl dark:ring-0
            "
					>
						🚫 No instances found.
					</div>
				) : (
					instanceList.map((inst) => (
						<InstanceCard
							key={inst.id}
							instance={inst}
							onToggle={handleAction}
							onConsole={handleConsole}
							loadingActions={loadingActions}
						/>
					))
				)}
			</div>
		</section>
	);
};

export default InstancesTable;
